import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { cloneDeep } from "lodash"
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/outline"

import RangeBillingPriceConfigTable from "../RangeBillingPriceConfigTable"
import { SERVICE_TYPES } from "../constants"
import { findOrCreateBillingService, getCurrency } from "../utils"

const NaturaPickAndPackServices = ({ storeBillingServices, onUpdateStoreBillingServices, onIsValid, storeId, warehouse }) => {
    const { i18n } = useTranslation()

    const tableHeaders = [
        { label: i18n.t("billing.table_headers.from_piece"), id: 'from' },
        { label: i18n.t("billing.table_headers.to_piece"), id: 'to' },
        { label: i18n.t("billing.table_headers.price"), id: 'price' },
        { label: i18n.t("billing.table_headers.unit"), id: 'unit' },
        { label: null, id: 'actions' }
    ]

    const [isPickAndPackValid, setIsPickAndPackValid] = useState(false)
    const lastEmittedValid = useRef(null)

    const pickAndPackService = findOrCreateBillingService({
        serviceType: SERVICE_TYPES.SERVICE_PICK_AND_PACK_NATURA_VENDA_DIRETA,
        billingServices: storeBillingServices,
        currency: getCurrency(warehouse?.country),
        storeId: storeId,
        warehouseId: warehouse?.id
    })

    const tableData = pickAndPackService?.price_configuration.map(priceRange => {
        return {
            from: { value: priceRange.from, isDisabled: true },
            to: { value: priceRange.to },
            price: { value: priceRange.price },
            price_mode: { value: priceRange.price_mode },
            excess: { value: priceRange.excess },
            range_exclusion: { value: priceRange.range_exclusion },
        }
    })

    const unitOptionsLabels = {
        PER_UNIT: i18n.t("billing.natura_services.pick_and_pack_unit_options.PER_UNIT"),
        TOTAL: i18n.t("billing.natura_services.pick_and_pack_unit_options.TOTAL"),
        EXCESS_PER_UNIT: i18n.t("billing.natura_services.pick_and_pack_unit_options.EXCESS_PER_UNIT"),
        EXCESS_TOTAL: i18n.t("billing.natura_services.pick_and_pack_unit_options.EXCESS_TOTAL")
    }

    const handleOnUpdateData = (newTableData, service) => {
        let newStoreBillingServices = cloneDeep(storeBillingServices)

        const newPriceConfiguration = newTableData.map(priceRangeData => {
            return {
                from: priceRangeData.from.value,
                to: priceRangeData.to.value,
                price: priceRangeData.price.value,
                price_mode: priceRangeData.price_mode.value,
                excess: priceRangeData.excess.value,
                range_exclusion: priceRangeData.range_exclusion.value
            }
        })

        const serviceExists = newStoreBillingServices.some(({ service_type, service_type_category }) => (
            service_type === service.service_type && service_type_category === service.service_type_category
        ))

        if (serviceExists) {
            newStoreBillingServices = newStoreBillingServices.map(billingService => {
                if (billingService.service_type === service.service_type && billingService.service_type_category === service.service_type_category) {
                    return { ...billingService, price_configuration: newPriceConfiguration }
                }
                return billingService
            })
        } else {
            const newService = cloneDeep(service)
            newService.price_configuration = newPriceConfiguration
            newStoreBillingServices.push(newService)
        }

        onUpdateStoreBillingServices(newStoreBillingServices)
    }

    useEffect(() => {
        if (lastEmittedValid.current !== isPickAndPackValid) {
            lastEmittedValid.current = isPickAndPackValid
            onIsValid(isPickAndPackValid)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- onIsValid is a stable parent callback, lastEmittedValid ref guards against stale calls
    }, [isPickAndPackValid])

    return (
        <div>
            <div className="flex gap-1">
                <h2 className="text-xl text-gray-500">
                    <span className="text-red-500">*</span>
                    {i18n.t("billing.natura_services.pick_and_pack_title")}
                </h2>
                {isPickAndPackValid ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : (
                    <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />
                )}
            </div>

            <RangeBillingPriceConfigTable
                headers={tableHeaders}
                priceConfiguration={tableData}
                currency={getCurrency(warehouse?.country)}
                onUpdatePriceConfiguration={newTableData => handleOnUpdateData(newTableData, pickAndPackService)}
                onIsValid={(isValid) => setIsPickAndPackValid(isValid)}
                unitOptionsLabels={unitOptionsLabels}
            />
        </div>
    )
}

export default NaturaPickAndPackServices
